import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { getQuizResultsCall, getStudentQuizProgressCall, listQuizAssignmentsCall } from "../../redux/api/quizSystem.api";
import ProfSelector from "components/ProfSelector";
import { useSelector } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export default function QuizResults() {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useSelector((state) => state?.auth);
  const role = auth?.role;
  const authUserId = auth?.id;
  const isProfRole = role === "prof" || role === "professor";
  const roleKey = String(role || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  const canSelectProf =
    roleKey === "admin" || roleKey === "university_admin" || roleKey === "university";
  const [results, setResults] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [assignmentId, setAssignmentId] = useState("");
  const [selectedProfId, setSelectedProfId] = useState(isProfRole && authUserId ? authUserId : "");
  const [profOptions, setProfOptions] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsStudent, setChartsStudent] = useState(null);
  const [chartsAttempts, setChartsAttempts] = useState([]);

  const allProfIdsForScope = useMemo(() => {
    if (selectedProfId) return [selectedProfId];
    if (!canSelectProf) return authUserId ? [authUserId] : [];
    const ids = profOptions.map((p) => p.id).filter(Boolean);
    if (authUserId) ids.push(authUserId);
    return Array.from(new Set(ids.map(String)));
  }, [selectedProfId, canSelectProf, profOptions, authUserId]);

  const loadAssignments = async () => {
    try {
      if (selectedProfId) {
        const res = await listQuizAssignmentsCall({ prof_id: selectedProfId });
        const list = res?.data ?? [];
        setAssignments(Array.isArray(list) ? list : []);
        return;
      }
      // Uni admin "All professors": merge each professor's assignments.
      if (canSelectProf && allProfIdsForScope.length > 0) {
        const bags = await Promise.all(
          allProfIdsForScope.map((id) => listQuizAssignmentsCall({ prof_id: id }))
        );
        const map = new Map();
        bags.forEach((res) => {
          const list = res?.data ?? [];
          (Array.isArray(list) ? list : []).forEach((a) => {
            if (a?.id) map.set(String(a.id), a);
          });
        });
        setAssignments(Array.from(map.values()));
        return;
      }
      const res = await listQuizAssignmentsCall({});
      const list = res?.data ?? [];
      setAssignments(Array.isArray(list) ? list : []);
    } catch (_) {
      setAssignments([]);
    }
  };

  const loadResults = async () => {
    setLoading(true);
    try {
      if (selectedProfId || !canSelectProf) {
        const res = await getQuizResultsCall({
          page: page + 1,
          limit: rowsPerPage,
          ...(assignmentId ? { assignment_id: assignmentId } : {}),
          ...(selectedProfId ? { prof_id: selectedProfId } : {}),
        });
        const data = res?.data ?? res;
        setResults(Array.isArray(data.results) ? data.results : []);
        setTotal(Number(data.total) || 0);
        return;
      }

      // Uni admin with "All professors": fetch each prof's submitted results and merge.
      if (allProfIdsForScope.length === 0) {
        setResults([]);
        setTotal(0);
        return;
      }

      const bags = await Promise.all(
        allProfIdsForScope.map((id) =>
          getQuizResultsCall({
            page: 1,
            limit: 100,
            ...(assignmentId ? { assignment_id: assignmentId } : {}),
            prof_id: id,
          })
        )
      );
      const merged = [];
      bags.forEach((res) => {
        const data = res?.data ?? res;
        const rows = Array.isArray(data?.results) ? data.results : [];
        merged.push(...rows);
      });
      merged.sort((a, b) => {
        const ta = a?.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const tb = b?.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return tb - ta;
      });
      setTotal(merged.length);
      const start = page * rowsPerPage;
      setResults(merged.slice(start, start + rowsPerPage));
    } catch (_) {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, [selectedProfId, allProfIdsForScope.join("|")]);

  useEffect(() => {
    loadResults();
  }, [page, rowsPerPage, assignmentId, selectedProfId, allProfIdsForScope.join("|")]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleString();
  };

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openProgressCharts = async () => {
    if (!menuRow?.user_id) {
      closeMenu();
      return;
    }
    const row = menuRow;
    closeMenu();
    setChartsOpen(true);
    setChartsStudent({ name: row.student_name, email: row.student_email });
    setChartsAttempts([]);
    setChartsLoading(true);
    try {
      const res = await getStudentQuizProgressCall(row.user_id);
      const payload = res?.data ?? res;
      const data = payload?.data ?? payload;
      setChartsStudent({
        name: data?.student_name ?? row.student_name,
        email: data?.student_email ?? row.student_email,
      });
      setChartsAttempts(Array.isArray(data?.attempts) ? data.attempts : []);
    } catch (_) {
      setChartsAttempts([]);
    } finally {
      setChartsLoading(false);
    }
  };

  const closeChartsModal = () => {
    setChartsOpen(false);
    setChartsStudent(null);
    setChartsAttempts([]);
  };

  const chartDefaults = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: {
          min: 0,
          max: 100,
          ticks: { stepSize: 20 },
        },
      },
    }),
    []
  );

  const toScorePercent = (v) => {
    if (v == null) return null;
    const n = Number(v);
    return n <= 1 && n >= 0 ? n * 100 : n;
  };

  const lineChartData = useMemo(() => {
    const attempts = [...(chartsAttempts || [])].reverse();
    return {
      labels: attempts.map((a) => (a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—")),
      datasets: [
        {
          label: "Score %",
          data: attempts.map((a) => toScorePercent(a.score) ?? 0),
          borderColor: "rgb(14, 165, 233)",
          backgroundColor: "rgba(14, 165, 233, 0.1)",
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [chartsAttempts]);

  const barChartData = useMemo(() => {
    const attempts = chartsAttempts || [];
    return {
      labels: attempts.map((a, i) => (a.quiz_title ? `${a.quiz_title.slice(0, 18)}${a.quiz_title.length > 18 ? "…" : ""}` : `Quiz ${i + 1}`)),
      datasets: [
        {
          label: "Score %",
          data: attempts.map((a) => toScorePercent(a.score) ?? 0),
          backgroundColor: "rgba(14, 165, 233, 0.8)",
          borderRadius: 4,
        },
      ],
    };
  }, [chartsAttempts]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems="center" gap={2} flexWrap="wrap">
          <MDTypography variant="h4" fontWeight="medium">
            {t("pages.quizResults.title")}
          </MDTypography>
          {canSelectProf && (
            <ProfSelector
              value={selectedProfId}
              onChange={(id) => {
                setSelectedProfId(id);
                setAssignmentId("");
                setPage(0);
              }}
              emptyLabel="All"
              onProfsLoaded={setProfOptions}
            />
          )}
          <FormControl
            size="small"
            sx={{
              minWidth: 240,
              "& .MuiOutlinedInput-root": {
                minHeight: 44,
                borderRadius: 2.5,
                bgcolor: theme.palette.background.paper || "#FFFFFF",
                boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                "& fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.35)",
                },
                "&:hover fieldset": {
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
                  "& fieldset": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1,
                  },
                },
              },
              "& .MuiInputLabel-root": {
                fontWeight: 600,
                color: theme.palette.text.secondary,
              },
            }}
          >
            <InputLabel id="quiz-filter-label" shrink>
              {t("pages.quizResults.selectAssignment")}
            </InputLabel>
            <Select
              labelId="quiz-filter-label"
              value={assignmentId}
              label={t("pages.quizResults.selectAssignment")}
              displayEmpty
              notched
              renderValue={(selected) => {
                if (!selected) return "All quizzes";
                const a = assignments.find((x) => String(x.id) === String(selected));
                return a?.title || a?.access_code || String(selected);
              }}
              onChange={(e) => {
                setAssignmentId(e.target.value);
                setPage(0);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <AssignmentOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.primary.main,
                      opacity: 0.9,
                    }}
                  />
                </InputAdornment>
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2.5,
                    mt: 1,
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                    "& .MuiMenuItem-root": {
                      borderRadius: 1.5,
                      mx: 0.5,
                      my: 0.25,
                      minHeight: 44,
                      py: 1.5,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All quizzes</MenuItem>
              {assignments.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.title || a.access_code || a.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><MDTypography variant="caption" fontWeight="bold">{t("pages.quizResults.student")}</MDTypography></TableCell>
                  <TableCell><MDTypography variant="caption" fontWeight="bold">{t("pages.professors.email")}</MDTypography></TableCell>
                  <TableCell><MDTypography variant="caption" fontWeight="bold">{t("pages.quizResults.assignment")}</MDTypography></TableCell>
                  <TableCell align="right"><MDTypography variant="caption" fontWeight="bold">{t("pages.quizResults.score")}</MDTypography></TableCell>
                  <TableCell align="right"><MDTypography variant="caption" fontWeight="bold">Correct / Total</MDTypography></TableCell>
                  <TableCell><MDTypography variant="caption" fontWeight="bold">Submitted</MDTypography></TableCell>
                  <TableCell align="center" padding="none" width={56}><MDTypography variant="caption" fontWeight="bold">Actions</MDTypography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <MDTypography variant="body2" color="text">Loading…</MDTypography>
                    </TableCell>
                  </TableRow>
                ) : results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <MDTypography variant="body2" color="text">No submitted results yet.</MDTypography>
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{row.student_email}</TableCell>
                      <TableCell>{row.quiz_title}</TableCell>
                      <TableCell align="right">
                        {row.score != null ? `${Number(row.score).toFixed(1)}%` : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {row.correct_count != null && row.total_questions != null
                          ? `${row.correct_count} / ${row.total_questions}`
                          : "—"}
                      </TableCell>
                      <TableCell>{formatDate(row.submitted_at)}</TableCell>
                      <TableCell align="center" padding="none">
                        {row.user_id ? (
                          <IconButton
                            size="small"
                            onClick={(e) => openMenu(e, row)}
                            aria-label="Open menu"
                            sx={{ color: "text.secondary" }}
                          >
                            <Icon>more_vert</Icon>
                          </IconButton>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Rows:"
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": { minHeight: 44, py: 1.5 },
                      "& .MuiList-root": { py: 0 },
                    },
                  },
                },
                sx: { minHeight: 40 },
              }}
              sx={{
                color: "text.primary",
                minHeight: 56,
                "& .MuiTablePagination-toolbar": { minHeight: 56 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiTablePagination-displayedRows, & .MuiTablePagination-actions":
                  { color: "text.primary" },
                "& .MuiIconButton-root": { color: "text.primary" },
                "& .MuiSelect-select": { minHeight: 28, py: 1.25 },
                "& .MuiTablePagination-selectRoot": { minHeight: 44 },
              }}
            />
          )}
        </Card>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={openProgressCharts}>Progress charts</MenuItem>
        </Menu>

        <Dialog open={chartsOpen} onClose={closeChartsModal} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Progress charts</span>
            <IconButton size="small" onClick={closeChartsModal} aria-label="close">
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {chartsStudent && (
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">{chartsStudent.name}</MDTypography>
                <MDTypography variant="body2" color="text">{chartsStudent.email}</MDTypography>
              </MDBox>
            )}
            {chartsLoading ? (
              <MDTypography variant="body2" color="text">Loading…</MDTypography>
            ) : chartsAttempts.length === 0 ? (
              <MDTypography variant="body2" color="text">No quiz attempts to chart.</MDTypography>
            ) : (
              <MDBox sx={{ width: "100%", minWidth: 0 }}>
                <MDTypography variant="button" color="text" fontWeight="bold" mb={1}>
                  Score over time (by attempt date)
                </MDTypography>
                <MDBox sx={{ width: "100%", height: 260, minHeight: 260, position: "relative", mb: 3 }}>
                  <Line key={`line-${chartsAttempts.length}`} data={lineChartData} options={chartDefaults} />
                </MDBox>
                <MDTypography variant="button" color="text" fontWeight="bold" mb={1}>
                  Score per quiz
                </MDTypography>
                <MDBox sx={{ width: "100%", height: 260, minHeight: 260, position: "relative" }}>
                  <Bar key={`bar-${chartsAttempts.length}`} data={barChartData} options={chartDefaults} />
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
